import React from "react";
import { useState } from "react";
import { useMemo } from "react";

import useSortableData from "../../../hooks/personnaliser/useSortableData";
import { Users, User, Search, Filter, ChevronDown, RotateCcw, Send } from "lucide-react";
import MatiereBadge from "./ComponentAdminGroupe/MatiereBadge";
import { estGroupeDeBesoin } from "../../../utils/groupesDeBesoin";

function DetailGroupe ({handleDetailGroupeClick, selectedGroupe , listGroupes, handleUpdateEleveGroupe}) {

  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [selectedItems, setSelectedItems] = useState({})
  // Le handle Change de la selection de noms de groupes.

  // Mémorisation des noms des classes en majuscules du groupe sélectionné
  const selectedClassesUpper = useMemo(() => {
    return selectedGroupe.classes_detail.map(classe => classe.toUpperCase());
  }, [selectedGroupe]); // Dépendance : selectedGroupe

  // Mémorisation du filtrage des groupes
  const groupefiltrées = useMemo(() => {
    return listGroupes.filter(groupe =>
      // Groupes de besoin seulement : viser un demi-groupe ajoute l'élève sans le
      // retirer de sa barrette, l'écran dirait « déplacé » à tort.
      estGroupeDeBesoin(groupe) &&
      // Même matière que le groupe courant : on ne switche pas un élève vers une autre matière.
      groupe.matiere === selectedGroupe.matiere &&
      groupe.classes_detail.some(classe =>
        selectedClassesUpper.includes(classe.toUpperCase())
      )
    );
  }, [listGroupes, selectedClassesUpper, selectedGroupe]); // + matière du groupe courant

  // Etat pour gérer le tri selon quel champ de façon croissante ou décroissante.
  const { items: sortedGroupes, requestSort, sortConfig } = useSortableData(selectedGroupe.eleves);

  // Filtrage des élèves
  const filteredEleves = sortedGroupes.filter(eleve => {
    const matchesSearch = `${eleve.nom} ${eleve.prenom}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = filterClass === 'all' || eleve.classe_nom === filterClass;
    return matchesSearch && matchesClass;
  });

  const handleUpdate = () => {
    // selectedItems est de la forme : { 1045: '70', 1047: '70', 1391: '70' }
    const groupedAffectations = Object.keys(selectedItems).reduce((acc, eleveId) => {
      const groupId = selectedItems[eleveId];
      if (!acc[groupId]) {
        acc[groupId] = [];
      }
      acc[groupId].push(parseInt(eleveId, 10));
      return acc;
    }, {});

    // Puis, transformer ce regroupement en tableau d'objets
    const affectations = {
      affectations: Object.entries(groupedAffectations).map(([groupId, elevesIds]) => ({
        groupId, // ou parseInt(groupId, 10) si vous préférez un nombre
        elevesIds,
      })),
    };

    //handleUpdateEleveGroupe(selectedGroupe.id, selectedItems)
    handleUpdateEleveGroupe(affectations)
  }

  const handleSelectionChange = (eleveId, newValue) => {
    setSelectedItems(prev => {
      // Si newValue est une chaîne vide, retirez la clé correspondante
      if (newValue === "") {
        const { [eleveId]: _, ...rest } = prev; // Utilisez la déstructuration pour exclure la clé `eleveId`
        return rest; // Retourne l'objet sans la clé `eleveId`
      } else {
        // Sinon, mettez à jour ou ajoutez la clé avec la nouvelle valeur
        return { ...prev, [eleveId]: newValue };
      }
    });
  };

  return(
    <div className="flex flex-col w-full px-2 sm:px-4 lg:px-6">
   
      {/* Header - Responsive */}
      <div className="bg-gradient-to-r from-blue-500 to-sky-600 rounded-xl p-3 sm:p-4 lg:p-6 xl:p-4 2xl:p-5 3xl:p-6 text-white mb-4 sm:mb-5 shadow-lg">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-5">
          <button
            className="px-3 py-2 sm:px-4 sm:py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-all duration-200 hover:-translate-y-0.5 text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm self-start"
            onClick={handleDetailGroupeClick}
          >
            ← RETOUR
          </button>

          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-base sm:text-lg lg:text-xl xl:text-base 2xl:text-lg 3xl:text-xl font-bold text-white flex items-center gap-2 flex-wrap">
                  <span>Détail du groupe : {selectedGroupe.nom}</span>
                  {selectedGroupe.matiere && <MatiereBadge matiere={selectedGroupe.matiere} />}
                </h1>
                <p className="text-xs sm:text-sm lg:text-base xl:text-xs 2xl:text-sm 3xl:text-base text-blue-100">
                  Gestion des élèves et changements de groupe
                </p>
              </div>
            </div>
          </div>

          {Object.keys(selectedItems).length !== 0 && (
            <button
              onClick={handleUpdate}
              className="bg-white/20 text-sm sm:text-base lg:text-lg xl:text-xs 2xl:text-sm 3xl:text-lg hover:bg-white/30 backdrop-blur-sm border border-white/30 px-3 sm:px-4 lg:px-6 xl:px-4 2xl:px-5 3xl:px-6 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3
              rounded-lg font-medium transition-all duration-200 hover:-translate-y-0.5 flex items-center gap-2 self-start sm:self-center"
            >
              <Send size={15} />
              <span className="hidden sm:inline">VALIDER LES CHANGEMENTS</span>
              <span className="sm:hidden">VALIDER</span>
            </button>
          )}
        </div>
      </div>
  
      {/* Stats Cards - Responsive Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 xl:gap-3 2xl:gap-4 3xl:gap-6 mb-4 sm:mb-6 lg:mb-8 xl:mb-4 2xl:mb-6 3xl:mb-8">
        <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 lg:p-6 xl:p-3 2xl:p-4 3xl:p-6 shadow-lg border border-gray-200">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 xl:w-8 xl:h-8 2xl:w-9 2xl:h-9 3xl:w-10 3xl:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="text-blue-600" size={16} />
            </div>
            <div>
              <p className="text-lg sm:text-xl lg:text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-gray-900">{selectedGroupe.eleves.length}</p>
              <p className="text-xs sm:text-sm xl:text-[10px] 2xl:text-xs 3xl:text-sm text-gray-600">Élèves total</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 lg:p-6 xl:p-3 2xl:p-4 3xl:p-6 shadow-lg border border-gray-200">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 xl:w-8 xl:h-8 2xl:w-9 2xl:h-9 3xl:w-10 3xl:h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <User className="text-green-600" size={16} />
            </div>
            <div>
              <p className="text-lg sm:text-xl lg:text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-gray-900">{selectedGroupe.classes_detail[0][0]}</p>
              <p className="text-xs sm:text-sm xl:text-[10px] 2xl:text-xs 3xl:text-sm text-gray-600">Niveau</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 lg:p-6 xl:p-3 2xl:p-4 3xl:p-6 shadow-lg border border-gray-200">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 xl:w-8 xl:h-8 2xl:w-9 2xl:h-9 3xl:w-10 3xl:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <span className="text-purple-600 font-bold text-sm sm:text-base xl:text-xs 2xl:text-sm 3xl:text-base">{selectedGroupe.classes_detail.length}</span>
            </div>
            <div>
              <p className="text-lg sm:text-xl lg:text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-gray-900">Classes</p>
              <p className="text-xs sm:text-sm xl:text-[10px] 2xl:text-xs 3xl:text-sm text-gray-600 truncate">{selectedGroupe.classes_detail.map((c)=> c + ' ')}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 lg:p-6 xl:p-3 2xl:p-4 3xl:p-6 shadow-lg border border-gray-200">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 xl:w-8 xl:h-8 2xl:w-9 2xl:h-9 3xl:w-10 3xl:h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <span className="text-orange-600 font-bold text-sm sm:text-base xl:text-xs 2xl:text-sm 3xl:text-base">{selectedItems && Object.keys(selectedItems).length}</span>
            </div>
            <div>
              <p className="text-lg sm:text-xl lg:text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-gray-900">Sélectionnés</p>
              <p className="text-xs sm:text-sm xl:text-[10px] 2xl:text-xs 3xl:text-sm text-gray-600">Pour transfert</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls - Responsive */}
      <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg border border-gray-200 p-3 sm:p-4 lg:p-6 xl:p-3 2xl:p-4 3xl:p-6 mb-4 sm:mb-6 lg:mb-8 xl:mb-4 2xl:mb-6 3xl:mb-8">
        <div className="flex flex-col gap-3 sm:gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
          {/* Search and Filter Row */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Rechercher un élève..."
                className="w-full pl-10 pr-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-sm sm:text-base lg:text-lg xl:text-xs 2xl:text-sm 3xl:text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <select
                className="w-full sm:w-auto pl-10 pr-8 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-sm sm:text-base lg:text-lg xl:text-xs 2xl:text-sm 3xl:text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white min-w-[150px]"
                value={filterClass}
                onChange={(e) => setFilterClass(e.target.value)}
              >
                <option value="all">Toutes les classes</option>
                {selectedGroupe.classes_detail.map(classe => (
                  <option key={classe} value={classe}>{classe}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            </div>
          </div>

          {/* Réinitialiser les sélections de groupe en cours */}
          {Object.keys(selectedItems).length > 0 && (
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-3">
              <button
                onClick={() => setSelectedItems({})}
                className="px-3 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-sm sm:text-base lg:text-lg xl:text-xs 2xl:text-sm 3xl:text-lg bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
              >
                <RotateCcw size={18} />
                Réinitialiser la sélection ({Object.keys(selectedItems).length})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table - Responsive with horizontal scroll on mobile */}
      <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto h-full">
          <table className="min-w-full">
            <thead className="bg-gradient-to-r from-blue-500 to-sky-600 text-white sticky top-0">
              <tr>
                <th
                  onClick={() => requestSort('nom')}
                  className="px-3 sm:px-4 lg:px-6 xl:px-3 2xl:px-4 3xl:px-6 py-3 sm:py-4 xl:py-2.5 2xl:py-3 3xl:py-4 text-left text-xs sm:text-sm xl:text-[10px] 2xl:text-[11px] 3xl:text-sm font-semibold uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors whitespace-nowrap"
                >
                  <div className="flex items-center gap-1 sm:gap-2">
                    Nom - Prénom
                    <span className="text-xs sm:text-sm xl:text-[10px] 2xl:text-[11px] 3xl:text-sm">
                      {sortConfig?.key === 'nom'
                        ? (sortConfig.direction === 'ascending' ? '🔼' : '🔽')
                        : '🔽'}
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => requestSort('classe_nom')}
                  className="px-3 sm:px-4 lg:px-6 xl:px-3 2xl:px-4 3xl:px-6 py-3 sm:py-4 xl:py-2.5 2xl:py-3 3xl:py-4 text-left text-xs sm:text-sm xl:text-[10px] 2xl:text-[11px] 3xl:text-sm font-semibold uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors whitespace-nowrap"
                >
                  <div className="flex items-center gap-1 sm:gap-2">
                    Classe
                    <span className="text-xs sm:text-sm xl:text-[10px] 2xl:text-[11px] 3xl:text-sm">
                      {sortConfig?.key === 'classe_nom'
                        ? (sortConfig.direction === 'ascending' ? '🔼' : '🔽')
                        : '🔽'}
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => requestSort('professeur_maths')}
                  className="hidden md:table-cell px-3 sm:px-4 lg:px-6 xl:px-3 2xl:px-4 3xl:px-6 py-3 sm:py-4 xl:py-2.5 2xl:py-3 3xl:py-4 text-left text-xs sm:text-sm xl:text-[10px] 2xl:text-[11px] 3xl:text-sm font-semibold uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors whitespace-nowrap"
                >
                  <div className="flex items-center gap-1 sm:gap-2">
                    Professeur
                    <span className="text-xs sm:text-sm xl:text-[10px] 2xl:text-[11px] 3xl:text-sm">
                      {sortConfig?.key === 'professeur_maths'
                        ? (sortConfig.direction === 'ascending' ? '🔼' : '🔽')
                        : '🔽'}
                    </span>
                  </div>
                </th>
                <th className="px-3 sm:px-4 lg:px-6 xl:px-3 2xl:px-4 3xl:px-6 py-3 sm:py-4 xl:py-2.5 2xl:py-3 3xl:py-4 text-center text-xs sm:text-sm xl:text-[10px] 2xl:text-[11px] 3xl:text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                  Nouveau groupe
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredEleves.map((eleve, index) => (
                <tr 
                  key={eleve.id} 
                  className={`transition-colors duration-200 hover:bg-blue-50 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}
                >
                  <td className="px-3 sm:px-4 lg:px-6 xl:px-3 2xl:px-4 3xl:px-6 py-3 sm:py-4 xl:py-2.5 2xl:py-3 3xl:py-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 xl:w-8 xl:h-8 2xl:w-9 2xl:h-9 3xl:w-10 3xl:h-10 bg-gradient-to-br from-blue-400 to-sky-300 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm xl:text-[10px] 2xl:text-xs 3xl:text-sm flex-shrink-0">
                        {eleve.nom.charAt(0)}{eleve.prenom.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 text-sm sm:text-base lg:text-lg xl:text-xs 2xl:text-sm 3xl:text-lg truncate">
                          {eleve.nom} {eleve.prenom}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-3 sm:px-4 lg:px-6 xl:px-3 2xl:px-4 3xl:px-6 py-3 sm:py-4 xl:py-2.5 2xl:py-3 3xl:py-4">
                    <span className="bg-orange-500 text-white px-2 sm:px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1 rounded-lg sm:rounded-xl text-xs sm:text-sm xl:text-[10px] 2xl:text-xs 3xl:text-sm font-medium inline-block">
                      {eleve.classe_nom}
                    </span>
                  </td>

                  <td className="hidden md:table-cell px-3 sm:px-4 lg:px-6 xl:px-3 2xl:px-4 3xl:px-6 py-3 sm:py-4 xl:py-2.5 2xl:py-3 3xl:py-4">
                    <div className="flex items-center gap-1 sm:gap-2 text-gray-700">
                      <User size={14} className="text-gray-500" />
                      <span className="text-xs sm:text-sm xl:text-[10px] 2xl:text-xs 3xl:text-sm font-medium truncate">{eleve.professeur_maths}</span>
                    </div>
                  </td>

                  <td className="px-3 sm:px-4 lg:px-6 xl:px-3 2xl:px-4 3xl:px-6 py-3 sm:py-4 xl:py-2.5 2xl:py-3 3xl:py-4">
                    <div className="flex justify-center">
                      <select
                        className={`px-2 sm:px-3 lg:px-4 xl:px-2 2xl:px-3 3xl:px-4 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-lg sm:rounded-xl border-2 font-medium text-xs sm:text-sm xl:text-[10px] 2xl:text-xs 3xl:text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          selectedItems[eleve.id]
                            ? 'bg-green-50 border-green-300 text-green-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600 hover:border-blue-300'
                        }`}
                        value={selectedItems[eleve.id] || ''}
                        onChange={(event) => handleSelectionChange(eleve.id, event.target.value)}
                      >
                        <option value="">Choisir...</option>
                        {groupefiltrées.map((groupe) => (
                          ((groupe.niveau === "6eme" || groupe.niveau === '5eme') && groupe.nom !== selectedGroupe.nom) && (
                            <option key={groupe.id} value={groupe.id}>
                              {groupe.nom}
                            </option>
                          )
                        ))}
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Message si sélections - Responsive */}
      {Object.keys(selectedItems).length > 0 && (
        <div className="mt-4 sm:mt-6 xl:mt-4 2xl:mt-5 3xl:mt-6 bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4 xl:p-3 2xl:p-3.5 3xl:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-6 h-6 sm:w-8 sm:h-8 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs sm:text-sm xl:text-[10px] 2xl:text-xs 3xl:text-sm">ℹ</span>
            </div>
            <p className="text-blue-800 font-medium text-sm sm:text-base lg:text-lg xl:text-xs 2xl:text-sm 3xl:text-lg">
              {Object.keys(selectedItems).length} élève(s) sélectionné(s) pour un changement de groupe.
              <br/>
              <span className="hidden sm:inline"> Cliquez sur "VALIDER LES CHANGEMENTS" pour confirmer.</span>
              <span className="sm:hidden">Cliquez sur "VALIDER" pour confirmer.</span>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default DetailGroupe;